import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Autocomplete, Button, Dialog, Grid, ListItemIcon, ListItemText, Menu, MenuItem, MenuList, TextField, Typography } from '@mui/material'
import { BundlesTable, CustomersTable } from './AssignBundlesTable'
import { RightSidePanel } from 'wed-components-beta'
import AssignBundlesDetails from './AssignBundlesDetails'
import { Add, AddCircle, FilterAlt } from '@mui/icons-material'
import { DateTime } from 'luxon'
import AssignBundlesFilters from './AssignBundlesFilters'
import CashFlow, { BillingsWithCashFlow } from '../CashFlow/CashFlow'
import { AutoCompleteOptions, useLookupTables } from '../../../../hooks/useLookupTables'
import PreLoader from '../../../PreLoader'
import { useAxios } from '../../../../hooks/useAxios'
import { NewAssignBundlesCustomers, NewViewBundle } from '../types'
import { formatNumberToMoney, quickSort } from '../../../Utils'

export type SuggestedBundles = {
  fk_id_program: number
  program_name: string
  fk_id_voucher_type: number
  voucher_type_name: string
  voucher_type_tag: string
  quantity: number
}

export type AssignedVouchers = {
  beneficiary_dependence_id: number
  bundle: {
    id: number
    bundle_name: string
    voucher_amount: number
  }
  fk_id_bundle: number
}

export type ExpectedVouchers = {
  affiliation_course_progression_name: string | null
  affiliation_course_progression_tag: string | null
  assigned_voucher_quantity: number
  credits: number
  expected_voucher_quantity: number
  fk_id_affiliation_course_progression: string | null
  fk_id_program: number
  fk_id_student: number
  fk_id_student_status_type?: number
  fk_id_voucher_type: number
  program_name: string
  student_status_type_name?: string
  voucher_type_name: string
  voucher_type_tag: string
}

type AutocompleteType = null | { id: number, label: string }

type BundlesFilter = {
  affiliation: AutocompleteType
  year: number
}

export type CustomerFilters = {
  affiliation: AutocompleteType
  grades: AutoCompleteOptions[]
  programs: AutoCompleteOptions[]
  credits: 'all' | number
  statuses: AutoCompleteOptions[]
  hideWithBundle: boolean
  hideAssignedEqualExpected: boolean
}

export type SortCustomers = {
  column: keyof NewAssignBundlesCustomers
  direction: 'asc' | 'desc'
}

export type Panel = {
  show: boolean
  fk_id_student?: number
}

export type Modal = {
  show: boolean
  fk_id_bundle?: number
  customers?: number[]
}

export default function AssignBundles2() {
  // Hooks
  const { lookupTables, errorLookupTables, isLoadingLookupTables } = useLookupTables(['affiliationCustomers', 'affiliations', 'grades', 'programs', 'referenceYearForBundles', 'studentStatusTypes'])
  // General
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null)
  const [panel, setPanel] = useState<Panel>({
    show: false
  })
  const [modal, setModal] = useState<Modal>({
    show: false
  })
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  // Data
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null)
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([])
  // Filters
  const [bundlesFilters, setBundlesFilters] = useState<BundlesFilter>({
    affiliation: null,
    year: DateTime.utc().get('year')
  })
  const [customerFilters, setCustomerFilters] = useState<CustomerFilters>({
    affiliation: null,
    grades: [],
    programs: [],
    credits: 'all',
    statuses: [],
    hideWithBundle: false,
    hideAssignedEqualExpected: false
  })
  const [sort, setSort] = useState<SortCustomers>({
    column: 'fk_id_grade',
    direction: 'asc'
  })

  // Bundles
  const bundlesUrl = useMemo(() => {
    const { affiliation, year } = bundlesFilters

    const qs: string[] = []

    if (!affiliation || !year) return ''
    qs.push(`fk_id_affiliation=${affiliation.id}`)
    qs.push(`year=${year}`)

    return `${process.env.REACT_APP_MAIN_BACKEND_URL}/bundles?${qs.join('&')}`
  }, [bundlesFilters])

  const { data: bundles, error: errorBundles, isLoading: isLoadingBundles } = useAxios<NewViewBundle[]>(bundlesUrl)

  console.log(bundles)

  const presetBundle = useMemo<BillingsWithCashFlow[]>(() => {
    if (!bundles || !selectedBundleId) return []

    return bundles
      .find((bundle) => bundle.id === selectedBundleId)!
      .default_billings
      .map((_) => ({
        billing: {
          fk_id_bundle: selectedBundleId,
          affiliation: { id: _.fk_id_affiliation, label: _.affiliation_name },
          defaultBilling: { id: _.id, label: _.default_billing_name },
          paymentMethod: _.fk_id_payment_method,
          issueDate: DateTime.utc(),
          service: { id: _.fk_id_service, label: _.service_name },
          beneficiaryType: 2, // Student
          beneficiary: null,
          payerType: _.fk_id_customer_type,
          payer: lookupTables?.affiliationCustomers.find((customer) => customer.dependence_id === customerFilters.affiliation?.id) || null,
          payee: _.fk_id_payee,
          referenceYear: bundlesFilters.year,
          currency: {
            id: _.fk_id_currency,
            sign: _.sign
          },
          totalBilling: {
            value: _.default_value,
            label: formatNumberToMoney(_.default_value)
          }
        },
        discount: {
          type: 'percentage',
          percentage: {
            value: 0,
            label: ''
          },
          setValue: {
            value: 0,
            label: ''
          }
        },
        cashFlowOptions: {
          mode: 'numberOfBills',
          numberOfBills: {
            value: _.max_installments,
            label: _.max_installments.toString()
          },
          setValue: {
            value: 0,
            label: ''
          },
          dateOfFirstCashflow: DateTime.utc(),
          remainder: 'first-bill'
        },
        cashFlowInstallments: []
      }))
  }, [bundles, selectedBundleId, bundlesFilters, customerFilters, lookupTables])

  // Customers
  const customersUrl = useMemo(() => {
    if (!customerFilters.affiliation) return ''

    return `${process.env.REACT_APP_MAIN_BACKEND_URL}/assign-bundles?fk_id_affiliation=${customerFilters.affiliation.id}&year=${bundlesFilters.year}`
  }, [bundlesFilters, customerFilters])

  const { data: customers, error: errorCustomers, isLoading: isLoadingCustomers } = useAxios<NewAssignBundlesCustomers[]>(customersUrl)

  console.log(customers)

  const creditsOptions = useMemo(() => {
    if (!customers) return []

    const allCreditValues = customers.flatMap((_) => _.credits).map((_) => _.credits)

    const set = new Set(allCreditValues)

    return Array.from(set).sort((a, b) => a - b)
  }, [customers])

  useEffect(() => {
    setSelectedCustomers([])
  }, [customers])

  // Bundles Filters
  const filterBundleByAffiliation = useCallback((bundle: NewViewBundle) => {
    switch (bundlesFilters.affiliation) {
      case null:
        return true
      default:
        return bundle.fk_id_affiliation === bundlesFilters.affiliation.id || bundle.default_billings[0]?.fk_id_affiliation === bundlesFilters.affiliation.id
    }
  }, [bundlesFilters.affiliation])

  const filterBundleByYear = useCallback((bundle: NewViewBundle) => {
    return bundle.reference_year === bundlesFilters.year
  }, [bundlesFilters.year])

  const filteredBundles = useMemo(() => {
    return bundles?.filter(filterBundleByAffiliation).filter(filterBundleByYear) || []
  }, [bundles, filterBundleByAffiliation, filterBundleByYear])

  // Customers Filters
  const filterCustomerByGrade = useCallback((customer: NewAssignBundlesCustomers) => {
    if (customerFilters.grades.length === 0) return true

    for (const grade of customerFilters.grades) {
      if (grade.id === customer.fk_id_grade) return true
    }

    return false
  }, [customerFilters.grades])

  const filterCustomerByProgram = useCallback((customer: NewAssignBundlesCustomers) => {
    if (customerFilters.programs.length === 0) return true

    for (const program of customerFilters.programs) {
      if (program.id === customer.fk_id_program) return true
    }

    return false
  }, [customerFilters.programs])

  const filterCustomerByCredits = useCallback((customer: NewAssignBundlesCustomers) => {
    switch (customerFilters.credits) {
      case 'all':
        return true
      default:
        return Boolean(customer.credits.find((credit) => credit.credits === customerFilters.credits))
    }
  }, [customerFilters.credits])

  const filterCustomerByAssignedEqualExpected = useCallback((customer: NewAssignBundlesCustomers) => {
    if (customerFilters.hideAssignedEqualExpected) {
      return Boolean(customer.vouchers.find((voucher) => voucher.assigned !== voucher.expected))
    }

    return true
  }, [customerFilters.hideAssignedEqualExpected])

  const filterCustomerByStatus = useCallback((customer: NewAssignBundlesCustomers) => {
    if (customerFilters.statuses.length === 0) return true

    for (const status of customerFilters.statuses) {
      if (status.id === customer.fk_id_student_status_type) return true
    }

    return false
  }, [customerFilters.statuses])

  const filteredCustomers = useMemo(() => {
    if (!customers) return []

    return quickSort(
      customers
        .filter(filterCustomerByProgram)
        .filter(filterCustomerByGrade)
        .filter(filterCustomerByCredits)
        .filter(filterCustomerByStatus)
        .filter(filterCustomerByAssignedEqualExpected),
      sort.column,
      sort.direction
    )
  }, [
    customers,
    filterCustomerByGrade,
    filterCustomerByProgram,
    filterCustomerByCredits,
    filterCustomerByStatus,
    filterCustomerByAssignedEqualExpected,
    sort
  ])

  // When the Bundles affiliation filter changes
  useEffect(() => {
    setSelectedBundleId(null)
    setCustomerFilters((_customerFilters) => ({ ..._customerFilters, affiliation: bundlesFilters.affiliation }))
  }, [bundlesFilters.affiliation])

  useEffect(() => {
    if (modal.show) setActionsMenuAnchor(null)
  }, [modal, setActionsMenuAnchor])

  if (isLoadingLookupTables) return <PreLoader height={'70vh'} />

  if (errorLookupTables || !lookupTables || errorCustomers || errorBundles) return <Alert severity='error'>Could not load Lookup Tables.</Alert>
  console.log('lookupTables.referenceYearForBundles', lookupTables.referenceYearForBundles)
  return <>
    {/* Modal */}
    <Dialog
      id='cash-flow'
      open={modal.show}
      onClose={() => setModal({ show: false })}
      maxWidth='xl'
    >
      <Grid container maxWidth='xl'>
        <CashFlow
          presetBundle={presetBundle}
          customerIds={modal.customers}
          onClose={() => setModal({ show: false })}
        />
      </Grid>
    </Dialog>
    {/* RightSidePanel */}
    <RightSidePanel
      state={panel.show}
      onClose={() => setPanel({ show: false })}
      title='Assign Bundles details'
      maxWidth='lg'
    >
      {
        panel.show ?
          <AssignBundlesDetails
            panel={panel}
            year={bundlesFilters.year}
          />
          :
          <></>
      }
    </RightSidePanel>
    {/* Bundles */}
    <Grid item xs={3.95}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography>
            Bundles
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            id='bundles-filter-affiliation'
            options={lookupTables.affiliations}
            renderInput={(params) => <TextField {...params} label='Affiliation' />}
            value={bundlesFilters.affiliation}
            onChange={(event, newValue) => setBundlesFilters({ ...bundlesFilters, affiliation: newValue })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id='bundles-filter-year'
            label='Year'
            value={bundlesFilters.year}
            onChange={(e) => setBundlesFilters({ ...bundlesFilters, year: Number(e.target.value) })}
            select
          >
            {
              // Years with bundles, plus the current and the next one (rollout)
              Array.from(new Set([...lookupTables.referenceYearForBundles, DateTime.utc().year, DateTime.utc().year + 1])).sort().map((year, index) => <MenuItem key={index} id={`year-option-${year}`} value={year}>{year}</MenuItem>)
            }
          </TextField>
        </Grid>
        <Grid item xs={12} maxHeight={'75vh'}>
          {
            isLoadingBundles ?
              <PreLoader height={'70vh'} />
              :
              <BundlesTable
                data={filteredBundles}
                selectedBundleId={selectedBundleId}
                setSelectedBundleId={setSelectedBundleId}
              />
          }
        </Grid>
      </Grid>
    </Grid>
    {/* Spacer */}
    <Grid item xs={.1} />
    {/* Customers */}
    <Grid item xs={7.95}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography>
            Students
          </Typography>
        </Grid>
        <Grid item xs={3.25}>
          <Autocomplete
            id='customer-filter-affiliation'
            options={lookupTables.affiliations}
            renderInput={(params) => <TextField {...params} label='Affiliation' />}
            value={customerFilters.affiliation}
            onChange={(event, newValue) => setCustomerFilters((prev) => ({ ...prev, affiliation: newValue }))}
          />
        </Grid>
        <Grid item xs={1.75}>
          <Autocomplete
            id='customer-filter-grades'
            options={lookupTables.grades}
            value={customerFilters.grades}
            onChange={(e, newValue) => setCustomerFilters((prev) => ({ ...prev, grades: newValue }))}
            renderInput={(params) => <TextField {...params} label="Grades" />}
            multiple
            disableCloseOnSelect
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <Autocomplete
            id='customer-filter-programs'
            options={lookupTables.programs}
            value={customerFilters.programs}
            onChange={(e, newValue) => setCustomerFilters((prev) => ({ ...prev, programs: newValue }))}
            renderInput={(params) => <TextField {...params} label="Programs" />}
            multiple
            disableCloseOnSelect
            fullWidth
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            id='bundles-filter-credits'
            label='Credits'
            value={customerFilters.credits}
            onChange={(e) => setCustomerFilters((prev) => ({ ...prev, credits: e.target.value === 'all' ? 'all' : Number(e.target.value) }))}
            select
          >
            <MenuItem id={`credits-option-all`} value='all'>All</MenuItem>
            {
              creditsOptions.map((_credits, index) => <MenuItem key={index} id={`credits-option-${_credits}`} value={_credits}>{_credits.toFixed(1)}</MenuItem>)
            }
          </TextField>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            id='bundles-filter-button-more'
            startIcon={<FilterAlt />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            More
          </Button>
          <Menu
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuList>
              <AssignBundlesFilters
                customerFilters={customerFilters}
                setCustomerFilters={setCustomerFilters}
                statusOptions={lookupTables.studentStatusTypes}
              />
            </MenuList>
          </Menu>
        </Grid>
        <Grid item xs={2}>
          <Button
            id='button-actions'
            startIcon={<AddCircle />}
            onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
            disabled={!selectedCustomers.length}
          >
            Actions
          </Button>
        </Grid>
        {/* Menu */}
        <Menu
          id='menu-actions'
          open={Boolean(actionsMenuAnchor)}
          anchorEl={actionsMenuAnchor}
          onClose={() => setActionsMenuAnchor(null)}
          slotProps={{ paper: { sx: { padding: 0 } } }}
        >
          <MenuList>
            <MenuItem
              id='actions-option-assign-bundle'
              onClick={() => setModal({ show: true, fk_id_bundle: selectedBundleId!, customers: selectedCustomers })}
              disabled={!Boolean(selectedBundleId)}
            >
              <ListItemIcon>
                <AddCircle />
              </ListItemIcon>
              <ListItemText>
                Assign Bundle
              </ListItemText>
            </MenuItem>

            <MenuItem
              id='actions-option-create-billing'
              onClick={() => setModal({ show: true, customers: selectedCustomers })}
            >
              <ListItemIcon>
                <Add />
              </ListItemIcon>
              <ListItemText>
                Create Billing
              </ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
        <Grid item xs={12} maxHeight={`calc(75vh + ${selectedCustomers.length * 40}px)`}>
          {
            isLoadingCustomers ?
              <PreLoader height={'70vh'} />
              :
              <CustomersTable
                data={customers || []}
                filteredData={filteredCustomers}
                selectedCustomers={selectedCustomers}
                setSelectedCustomers={setSelectedCustomers}
                sort={sort}
                setSort={setSort}
                setPanel={setPanel}
              />
          }
        </Grid>
      </Grid>
    </Grid>
  </>
}
